import { getCourtLabel, getFeaturedCourtLabel } from '@pickleballinc/lib/court-title';
import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { formatRoundText } from '@pickleballinc/ui/lib/formatRoundText';
import moment from 'moment';

import { publicEnv } from '@/lib/env';
import type { MatchExtended, MatchInfo } from '@/modules/ticker/types';

export type MatchCardType = 'TICKER' | 'MATCH' | 'RESULT' | 'MATCHV3';

export interface FormatMatchInfoProps {
  matchInfos: MatchInfo[];
  imagesWidth: number;
  imagesHeight: number;
  matchCardType?: MatchCardType;
}

export function formatMatchInfo({
  matchInfos,
  imagesWidth,
  imagesHeight,
  matchCardType,
}: FormatMatchInfoProps) {
  if (matchInfos.length === 0) return [];

  let filteredMatchInfos = [...matchInfos];
  if (matchCardType === 'TICKER') {
    filteredMatchInfos = matchInfos.filter((match) => {
      const tournamentLocalDate = moment().utc().add(Number(match.utcOffset), 'seconds');
      return (
        (match.localDateMatchCompleted &&
          tournamentLocalDate.isSame(moment(match.localDateMatchCompleted).utc(), 'day')) ||
        (match.localDateMatchAssignedToCourt &&
          tournamentLocalDate.isSame(moment(match.localDateMatchAssignedToCourt).utc(), 'day')) ||
        (match.localDateMatchStart &&
          tournamentLocalDate.isSame(moment(match.localDateMatchStart).utc(), 'day')) ||
        (match.localDateMatchPlannedStart &&
          tournamentLocalDate.isSame(moment(match.localDateMatchPlannedStart).utc(), 'day'))
      );
    });
  }

  const matches: MatchExtended[] = filteredMatchInfos.map((matchInfo) => {
    const teamOneScores = [matchInfo.teamOneGameOneScore];
    const teamTwoScores = [matchInfo.teamTwoGameOneScore];
    const gamesStatus: number[] = [+matchInfo.gameOneStatus];

    const teamOnePlayers = [
      {
        playerId: matchInfo.teamOnePlayerOneUuid,
        firstName: matchInfo.teamOnePlayerOneFirstName,
        lastName: matchInfo.teamOnePlayerOneLastName,
        suffixName: matchInfo.teamOnePlayerOneNameSuffix,
        image:
          matchCardType === 'MATCHV3'
            ? matchInfo.teamOnePlayerOnePicture
            : getCDNURL(matchInfo.teamOnePlayerOnePicture, imagesWidth, imagesHeight),
      },
    ];
    const teamTwoPlayers = [
      {
        playerId: matchInfo.teamTwoPlayerOneUuid,
        firstName: matchInfo.teamTwoPlayerOneFirstName,
        lastName: matchInfo.teamTwoPlayerOneLastName,
        suffixName: matchInfo.teamTwoPlayerOneNameSuffix,
        image:
          matchCardType === 'MATCHV3'
            ? matchInfo.teamTwoPlayerOnePicture
            : getCDNURL(matchInfo.teamTwoPlayerOnePicture, imagesWidth, imagesHeight),
      },
    ];

    if (matchInfo.teamOnePlayerTwoUuid) {
      teamOnePlayers.push({
        playerId: matchInfo.teamOnePlayerTwoUuid,
        firstName: matchInfo.teamOnePlayerTwoFirstName,
        lastName: matchInfo.teamOnePlayerTwoLastName,
        suffixName: matchInfo.teamOnePlayerTwoNameSuffix,
        image:
          matchCardType === 'MATCHV3'
            ? matchInfo.teamOnePlayerTwoPicture
            : getCDNURL(matchInfo.teamOnePlayerTwoPicture, imagesWidth, imagesHeight),
      });
    }

    if (matchInfo.teamTwoPlayerTwoUuid) {
      teamTwoPlayers.push({
        playerId: matchInfo.teamTwoPlayerTwoUuid,
        firstName: matchInfo.teamTwoPlayerTwoFirstName,
        lastName: matchInfo.teamTwoPlayerTwoLastName,
        suffixName: matchInfo.teamTwoPlayerTwoNameSuffix,
        image:
          matchCardType === 'MATCHV3'
            ? matchInfo.teamTwoPlayerTwoPicture
            : getCDNURL(matchInfo.teamTwoPlayerTwoPicture, imagesWidth, imagesHeight),
      });
    }

    if (matchInfo.scoreFormatGameBestOutOf > 1) {
      teamOneScores.push(matchInfo.teamOneGameTwoScore);
      teamTwoScores.push(matchInfo.teamTwoGameTwoScore);
      gamesStatus.push(+matchInfo.gameTwoStatus);
    }

    if (matchInfo.scoreFormatGameBestOutOf > 2) {
      teamOneScores.push(matchInfo.teamOneGameThreeScore);
      teamTwoScores.push(matchInfo.teamTwoGameThreeScore);
      gamesStatus.push(+matchInfo.gameThreeStatus);
    }

    if (matchInfo.scoreFormatGameBestOutOf > 3) {
      teamOneScores.push(matchInfo.teamOneGameFourScore);
      teamTwoScores.push(matchInfo.teamTwoGameFourScore);
      gamesStatus.push(+matchInfo.gameFourStatus);
    }

    if (matchInfo.scoreFormatGameBestOutOf > 4) {
      teamOneScores.push(matchInfo.teamOneGameFiveScore);
      teamTwoScores.push(matchInfo.teamTwoGameFiveScore);
      gamesStatus.push(+matchInfo.gameFiveStatus);
    }

    const formattedRoundText = formatRoundText({
      roundText: matchInfo.roundText,
      formatRound: true,
    });

    let matchObject: MatchExtended = {
      id: matchInfo.matchUuid,
      timezoneAbbreviation: matchInfo.timezoneAbbreviation,
      matchStatus: matchInfo.matchStatus,
      matchCompletedType: matchInfo.matchCompletedType,
      currentServingNumber: matchInfo.currentServingNumber,
      server: matchInfo.server,
      serverFromTeam: matchInfo.serverFromTeam,
      team1: {
        players: [...teamOnePlayers],
        //percentage: Math.round(matchInfo.teamOneWinningPercentage * 10) / 10,
        scores: [...teamOneScores],
        isWinner: matchInfo.matchStatus === 4 && matchInfo.winner === 1,
      },
      team2: {
        players: [...teamTwoPlayers],
        //percentage: Math.round((100 - matchInfo.teamOneWinningPercentage) * 10) / 10,
        scores: [...teamTwoScores],
        isWinner: matchInfo.matchStatus === 4 && matchInfo.winner === 2,
      },
      gamesStatus: gamesStatus,
      roundNumber:
        formattedRoundText || (matchInfo.roundNumber ? `Round ${matchInfo.roundNumber}` : ''),
      bracketsURL: `${publicEnv.NEXT_PUBLIC_BRACKETS_URL}/tournaments/${matchInfo.tournamentUuid}/events/${matchInfo.eventUuid}`,
      bracketsURLTarget: '_self',
    };

    if (matchCardType === 'TICKER') {
      matchObject = {
        ...matchObject,
        courtName: getFeaturedCourtLabel(matchInfo.courtTitle),
        tournamentTitle: matchInfo.tournamentTitle,
        matchTimeStart: formatDateTime(
          matchInfo?.localDateMatchCompleted ||
            matchInfo?.localDateMatchStart ||
            matchInfo?.localDateMatchPlannedStart,
          { type: 'time' }
        ),
      };
    } else if (matchCardType === 'MATCHV3') {
      matchObject = {
        ...matchObject,
        tournamentTitle: matchInfo.tournamentTitle,
        eventTitle: matchInfo.eventTitle,
        courtName: matchInfo.courtTitle ? `Court ${getCourtLabel(matchInfo.courtTitle)}` : '',
        matchTime: formatDateTime(
          matchInfo?.localDateMatchCompleted ||
            matchInfo?.localDateMatchStart ||
            matchInfo?.localDateMatchPlannedStart
        ),
        matchCardImageUrl: matchInfo.matchCardImageUrl,
      };
    } else {
      matchObject = {
        ...matchObject,
        eventTitle: matchInfo.eventTitle,
        courtName: matchInfo.courtTitle ? `Court ${getCourtLabel(matchInfo.courtTitle)}` : '',
        // This is commented out since matchTimeStart basically shows the same thing with the date as well
        // matchTime: formatDateTime(
        //     matchInfo?.localDateMatchCompleted ||
        //       matchInfo?.localDateMatchStart ||
        //       matchInfo?.localDateMatchPlannedStart,
        //   { type: 'time' }
        // ),
        matchTimeStart: formatDateTime(
          matchInfo?.localDateMatchCompleted ||
            matchInfo?.localDateMatchStart ||
            matchInfo?.localDateMatchPlannedStart,
          { type: 'datetime', includeYear: false }
        ),
      };
    }

    if (matchCardType === 'RESULT') {
      matchObject = {
        ...matchObject,
        gameOneEndDate: matchInfo.gameOneEndDate,
        gameTwoEndDate: matchInfo.gameTwoEndDate,
        gameThreeEndDate: matchInfo.gameThreeEndDate,
        gameFourEndDate: matchInfo.gameFourEndDate,
        gameFiveEndDate: matchInfo.gameFiveEndDate,
      };
    }

    return { ...matchObject, changeHappened: matchInfo.changeHappened };
  });

  return matches;
}
